import { useCallback, useState } from 'react'
import { useStorage } from '../providers/platform-provider'
import type { CV, CreateCVRequest, UpdateCVRequest } from '../types/cv'

interface UseCVOptions {
  /** Auto-save delay in milliseconds (0 to disable) */
  autoSaveDelay?: number
}

interface UseCVReturn {
  cv: CV | null
  isLoading: boolean
  isSaving: boolean
  error: Error | null
  loadCV: (id: string) => Promise<void>
  createCV: (data?: CreateCVRequest) => Promise<CV>
  updateCV: (data: UpdateCVRequest) => Promise<void>
  saveCV: () => Promise<void>
  /** Directly set CV data (for dev tools) */
  setCV: (cv: CV) => void
}

/**
 * Hook for managing a single CV
 */
export function useCV(_options: UseCVOptions = {}): UseCVReturn {
  const storage = useStorage()
  const [cv, setCV] = useState<CV | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const loadCV = useCallback(
    async (id: string) => {
      setIsLoading(true)
      setError(null)
      try {
        const loaded = await storage.getCV(id)
        setCV(loaded)
      } catch (e) {
        setError(e instanceof Error ? e : new Error('Failed to load CV'))
      } finally {
        setIsLoading(false)
      }
    },
    [storage]
  )

  const createCV = useCallback(
    async (data?: CreateCVRequest) => {
      setIsLoading(true)
      setError(null)
      try {
        const created = await storage.createCV(data)
        setCV(created)
        return created
      } catch (e) {
        const error = e instanceof Error ? e : new Error('Failed to create CV')
        setError(error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [storage]
  )

  const updateCV = useCallback(
    async (data: UpdateCVRequest) => {
      if (!cv) return

      // Optimistic update - merge personalInfo properly
      const optimisticCV: CV = {
        ...cv,
        ...data,
        personalInfo: data.personalInfo
          ? { ...cv.personalInfo, ...data.personalInfo }
          : cv.personalInfo,
        updatedAt: new Date().toISOString(),
      }
      setCV(optimisticCV)

      setIsSaving(true)
      setError(null)
      try {
        // Save to storage but don't update state again - trust optimistic update
        await storage.updateCV(cv.id, data)
      } catch (e) {
        setError(e instanceof Error ? e : new Error('Failed to update CV'))
        // Revert optimistic update
        await loadCV(cv.id)
      } finally {
        setIsSaving(false)
      }
    },
    [cv, storage, loadCV]
  )

  const saveCV = useCallback(async () => {
    if (!cv) return

    setIsSaving(true)
    setError(null)
    try {
      await storage.updateCV(cv.id, cv)
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Failed to save CV'))
    } finally {
      setIsSaving(false)
    }
  }, [cv, storage])

  return {
    cv,
    isLoading,
    isSaving,
    error,
    loadCV,
    createCV,
    updateCV,
    saveCV,
    setCV,
  }
}
